import React, { useState } from 'react';
import BaseModal from '../utils/BaseModal/BaseModal';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Container, Form, StyledButton } from './PostStoryModal.styles';
import { useDispatch } from 'react-redux';
import UploadMediaButton from '../../UploadMediaButton/UploadMediaButton';
import { ALBUM_MEDIA_TYPE, MANUAL_CHAT_SECRET, UPLOAD_FILE_CONSTRAINTS } from '../../../utils/constants';
import MediaItem from '../../MediaItem/MediaItem';
import { notifyError, notifyInfo } from '../../../utils/notify';
import { uploadStory } from '../../../store/slices/user/asyncThunks';
import Input from '../../utils/inputs/Input/Input';
import ErrorContainer from '../../utils/ErrorContainer/ErrorContainer';

const uploadBundleSchema = yup
  .object()
  .shape({
    description: yup.string().trim().required('Description is required'),
    secret: yup.string().trim().required('Secret is required'),
    media: yup.mixed().required('Media is required'),
  })
  .required();

const PostStoryModal = ({ modalRef, influencerId }) => {
  const dispatch = useDispatch();
  const [uploadErrors, setUploadErrors] = useState([]);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      description: '',
      secret: '',
      media: null,
    },
    delayError: 300,
    resolver: yupResolver(uploadBundleSchema),
  });

  const mediaWatch = watch('media');

  const onProgress = progress => {
    setProgress(progress > 99 ? 99 : progress.toFixed(1));
  };

  const onSubmit = async data => {
    setLoading(true);
    if (data.secret !== MANUAL_CHAT_SECRET) {
      notifyError('Wrong secret!');
      setLoading(false);
      return;
    }

    const description = data.description;
    const media = data.media;
    const kind = media.file.type.includes('video') ? ALBUM_MEDIA_TYPE.VIDEO : ALBUM_MEDIA_TYPE.PHOTO;

    dispatch(
      uploadStory({
        media,
        kind,
        description,
        onProgress,
        influencerId,
      }),
    )
      .unwrap()
      .then(() => {
        modalRef.current.hide();
        notifyInfo('Story posted successfully');
      })
      .catch(error => {
        setLoading(false);
        setProgress(0);
        notifyError(error.message);
      });
  };

  return (
    <BaseModal
      title="Post Story"
      ref={modalRef}
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        reset();
        setProgress(0);
        setLoading(false);
      }}>
      <Container>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="Description"
            placeholder="Enter description"
            {...register('description')}
            error={errors.description?.message}
          />
          <Input label="Secret" placeholder="Enter secret" {...register('secret')} error={errors.secret?.message} />
          {mediaWatch ? (
            <MediaItem
              type={UPLOAD_FILE_CONSTRAINTS.TYPE.STORY}
              kind={mediaWatch.file.type.includes('video') ? ALBUM_MEDIA_TYPE.VIDEO : ALBUM_MEDIA_TYPE.PHOTO}
              file={mediaWatch}
              progress={progress}
              onDelete={() => {
                setValue('media', null);
                setUploadErrors(prev => prev.filter(error => error.fileName !== mediaWatch.file.name));
              }}
              uploadError={uploadErrors.find(error => error.fileName === mediaWatch.file.name)}
            />
          ) : (
            <Controller
              name="media"
              control={control}
              render={({ field }) => {
                return (
                  <UploadMediaButton
                    disabled={loading}
                    type={UPLOAD_FILE_CONSTRAINTS.TYPE.STORY}
                    onChange={files => {
                      field.onChange(files[0]);
                    }}
                    setError={setUploadErrors}
                    multiple={false}
                  />
                );
              }}
            />
          )}
          <ErrorContainer errorText={errors.media?.message} />
          <StyledButton title="Post" isLoading={loading} disabled={uploadErrors.length} />
        </Form>
      </Container>
    </BaseModal>
  );
};

export default PostStoryModal;
